/**
 * Loading a record and getting it playing.
 *
 * The visual swap and the audio load run together; the needle only comes down
 * once both have finished, which is what makes the cinematic swap feel like
 * one motion rather than two.
 */

import { resolveArtwork } from '../data/artwork.ts';
import * as player from '../state/player.ts';
import { prefsSnapshot } from '../state/prefs.ts';
import { accentFor, applyAccent, ringAccentFor, setYoutubeCardOpen, toast } from '../state/ui.ts';
import type { Track } from '../types/media.ts';
import { hardware } from './hardware.ts';

export interface LoadOptions {
  autoplay?: boolean;
  /** Overrides the user's cinematic-swap preference for this load. */
  cinematic?: boolean;
}

/** Guards against an older load finishing after a newer one started. */
let loadSeq = 0;

export async function loadTrack(
  track: Track,
  queue: readonly Track[] | null,
  index: number | null,
  { autoplay = true, cinematic }: LoadOptions = {},
): Promise<void> {
  const { tt } = hardware();
  const prefs = prefsSnapshot();
  const seq = ++loadSeq;

  if (queue) {
    player.deck.setQueue(queue, index ?? 0);
    player.setQueue(player.deck.queue);
  }

  const art = await resolveArtwork(track);
  if (seq !== loadSeq) return;

  player.setTrack(track);
  player.setArtwork(art);
  applyAccent(accentFor(art.palette));
  tt.setAccent(ringAccentFor(art.palette));
  setYoutubeCardOpen(track.source === 'youtube');
  player.setPosition(0);
  player.setDuration(track.duration || 0);
  tt.setProgress(0);

  /* The audio is prepared while the visual swap plays out. */
  const prepared = player.deck.prepare(track, index).catch((e: unknown) => {
    toast(messageOf(e, 'Could not load that track.'));
  });

  await Promise.all([
    tt.swapRecord(art.image, art.palette, {
      cinematic: cinematic ?? prefs.cinematicSwap,
      pictureDisc: prefs.pictureDisc,
    }),
    prepared,
  ]);
  if (seq !== loadSeq) return;

  if (autoplay) await startPlayback(seq);
  else {
    tt.setSpinning(false);
    tt.parkArm();
  }
}

/** Spin up, cue the needle, then let sound out — in that order. */
export async function startPlayback(seq = loadSeq): Promise<void> {
  const { tt } = hardware();
  tt.setPowered(true);
  tt.setSpinning(true);

  if (prefsSnapshot().cinematicSwap && !tt.state.armDown) {
    void player.deck.demo.sfxNeedleDrop().catch(() => undefined);
    await tt.dropNeedle();
    if (seq !== loadSeq) return;
  } else {
    tt.cueArm();
    void tt.setArmDown(true, 220);
  }

  await play();
}

/** The arm has been set down on the record by hand: the needle drops where it is. */
export async function playFromArm(): Promise<void> {
  const { tt } = hardware();
  tt.setPowered(true);
  tt.setSpinning(true);
  tt.cueArm();
  void player.deck.demo.sfxNeedleDrop().catch(() => undefined);
  void tt.setArmDown(true, 220);
  await play();
}

/** The arm has been carried to its rest by hand: the deck stops, and the arm settles. */
export async function stopAtRest(): Promise<void> {
  const { tt } = hardware();
  player.deck.pause();
  tt.setSpinning(false);
  if (await tt.restArm()) void player.deck.demo.sfxArmRest().catch(() => undefined);
}

async function play(): Promise<void> {
  try {
    await player.deck.play();
  } catch (e) {
    toast(messageOf(e, 'Playback failed.'));
  }
}

export async function stopPlayback(): Promise<void> {
  const { tt } = hardware();
  player.deck.pause();
  tt.setSpinning(false);
  await tt.liftNeedle();
}

/** Move `step` records through the crate. Returns false at the end of it. */
export async function skip(step: number, { auto = false } = {}): Promise<boolean> {
  const next = player.deck.playlist.next(step);
  const track = next < 0 ? null : player.deck.playlist.at(next);
  if (!track) {
    await stopPlayback();
    if (auto) toast('End of the crate');
    return false;
  }
  await loadTrack(track, null, next, { autoplay: true });
  return true;
}

export const messageOf = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback;
